import React, { useEffect, useRef, useState } from 'react';
import './AccountModal.css';
import { useAuth } from '../context/AuthContext';
import CityAutocompleteInput from './CityAutocompleteInput';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function AccountModal({ open, onClose }) {
  const {
    deleteAccount,
    isAuthenticated,
    isPasswordRecovery,
    profile,
    sendPasswordReset,
    signIn,
    signOut,
    signUp,
    updatePassword,
    updateProfile,
  } = useAuth();
  const [mode, setMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [cityDisplay, setCityDisplay] = useState('');
  const [location, setLocation] = useState({ city: '', state: '', country: '', selected: false });
  const [confirmedLocation, setConfirmedLocation] = useState({ city: '', state: '', country: '', display: '' });
  const [submitting, setSubmitting] = useState(false);
  const [didSaveProfile, setDidSaveProfile] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const dialogRef = useRef(null);

  const splitName = (value) => {
    const parts = String(value || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    return {
      firstName: parts[0] || '',
      lastName: parts.slice(1).join(' '),
    };
  };

  const getSignupFullName = () => [firstName.trim(), lastName.trim()].filter(Boolean).join(' ');
  const formatLocationDisplay = (nextCity, nextState, nextCountry) =>
    [nextCity, nextState, nextCountry].filter(Boolean).join(', ');

  useEffect(() => {
    if (!open) return;

    const scrollY = window.scrollY;
    const body = document.body;
    const previousStyles = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };

    body.classList.add('no-scroll');
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';

    return () => {
      body.classList.remove('no-scroll');
      body.style.position = previousStyles.position;
      body.style.top = previousStyles.top;
      body.style.left = previousStyles.left;
      body.style.right = previousStyles.right;
      body.style.width = previousStyles.width;
      body.style.overflow = previousStyles.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    setErrorMsg('');
    setSuccessMsg('');
    setPassword('');
    setShowDeleteConfirm(false);
    setDidSaveProfile(false);

    if (isPasswordRecovery) {
      setMode('reset');
    } else if (!isAuthenticated) {
      setMode('signin');
      setEmail('');
      setFirstName('');
      setLastName('');
      setCityDisplay('');
      setLocation({ city: '', state: '', country: '', selected: false });
      setConfirmedLocation({ city: '', state: '', country: '', display: '' });
    }
  }, [open, isAuthenticated, isPasswordRecovery]);

  useEffect(() => {
    if (!open || !isAuthenticated || !profile || isPasswordRecovery) return;

    const parsedName = splitName(profile.fullName || '');
    setMode('account');
    setEmail(profile.email || '');
    setFirstName(profile.firstName || parsedName.firstName);
    setLastName(profile.lastName || parsedName.lastName);
    const nextDisplay = formatLocationDisplay(profile.city || '', profile.state || '', profile.country || '');
    setCityDisplay(nextDisplay);
    setLocation({
      city: profile.city || '',
      state: profile.state || '',
      country: profile.country || '',
      selected: !!profile.city,
    });
    setConfirmedLocation({
      city: profile.city || '',
      state: profile.state || '',
      country: profile.country || '',
      display: nextDisplay,
    });
  }, [open, isAuthenticated, profile, isPasswordRecovery]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const inferRegionFromText = (value) => {
    const match = String(value || '')
      .trim()
      .match(/^\s*(.+?),\s*([A-Za-z]{2})(?:\s*,\s*([A-Za-z\s]+))?\s*$/);

    if (!match) {
      return {
        city: String(value || '').trim(),
        state: '',
        country: '',
      };
    }

    return {
      city: match[1].trim(),
      state: match[2].toUpperCase(),
      country: match[3]?.trim() || 'United States',
    };
  };

  const getResolvedLocation = () => {
    const normalizedDisplay = cityDisplay.trim().toLowerCase();
    const confirmedDisplay = confirmedLocation.display.trim().toLowerCase();

    if (
      confirmedLocation.city &&
      confirmedDisplay &&
      (
        !normalizedDisplay ||
        (confirmedDisplay.startsWith(normalizedDisplay) && confirmedDisplay.length > normalizedDisplay.length) ||
        (confirmedLocation.state && !normalizedDisplay.includes(confirmedLocation.state.toLowerCase())) ||
        (confirmedLocation.country && !normalizedDisplay.includes(confirmedLocation.country.toLowerCase()))
      )
    ) {
      return {
        city: confirmedLocation.city,
        state: confirmedLocation.state,
        country: confirmedLocation.country,
      };
    }

    if (location.selected && location.city.trim()) {
      return {
        city: location.city.trim(),
        state: location.state || '',
        country: location.country || '',
      };
    }

    return inferRegionFromText(cityDisplay);
  };

  const handleLocationInputChange = (value, options = {}) => {
    setCityDisplay(value);
    if (!options.preserveSelection) {
      setLocation({ city: '', state: '', country: '', selected: false });
      setConfirmedLocation({ city: '', state: '', country: '', display: '' });
    }
    setDidSaveProfile(false);
    setSuccessMsg('');
  };

  const handleLocationSelect = ({ city, state, country, display }) => {
    setCityDisplay(display);
    setLocation({
      city: city || '',
      state: state || '',
      country: country || '',
      selected: true,
    });
    setConfirmedLocation({
      city: city || '',
      state: state || '',
      country: country || '',
      display,
    });
    setErrorMsg('');
  };

  const validateAuthFields = () => {
    if (!EMAIL_REGEX.test(email.trim())) {
      setErrorMsg('Enter a valid email address.');
      return false;
    }

    if (mode !== 'forgot' && password.trim().length < 6) {
      setErrorMsg('Password must be at least 6 characters.');
      return false;
    }

    if (mode === 'signup') {
      const resolvedLocation = getResolvedLocation();

      if (!resolvedLocation.city) {
        setErrorMsg('Choose a city from autocomplete, or type City, ST.');
        return false;
      }
    }

    return true;
  };

  const handleOverlayClick = (event) => {
    if (event.target === dialogRef.current) {
      onClose?.();
    }
  };

  const handleAuthSubmit = async (event) => {
    event.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');

    if (!validateAuthFields()) return;

    try {
      setSubmitting(true);

      if (mode === 'forgot') {
        await sendPasswordReset(email);
        setSuccessMsg('If that email is on file, a password reset link has been sent.');
        return;
      }

      if (mode === 'signup') {
        const resolvedLocation = getResolvedLocation();
        const result = await signUp({
          email,
          password,
          fullName: getSignupFullName(),
          firstName,
          lastName,
          city: resolvedLocation.city,
          state: resolvedLocation.state,
          country: resolvedLocation.country,
        });

        if (!result.session) {
          setErrorMsg('Email confirmation is enabled in Supabase. Disable it in Authentication > Providers > Email to allow immediate sign-in.');
          return;
        }

        setSuccessMsg('Account created. You are now signed in.');
      } else {
        await signIn({ email, password });
        setSuccessMsg('Signed in successfully.');
      }

      setPassword('');
      setTimeout(() => {
        onClose?.();
      }, 700);
    } catch (error) {
      console.error('Account auth failed:', error);
      if (String(error?.message || '').toLowerCase().includes('email not confirmed')) {
        setErrorMsg('Email confirmation is enabled in Supabase. Disable it in Authentication > Providers > Email if you want users to sign in immediately.');
      } else {
        setErrorMsg(error.message || 'Unable to complete that request.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordResetSubmit = async (event) => {
    event.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');

    if (password.trim().length < 6) {
      setErrorMsg('Password must be at least 6 characters.');
      return;
    }

    try {
      setSubmitting(true);
      await updatePassword(password);
      setPassword('');
      setSuccessMsg('Password updated. You can use your new password now.');
      setMode('account');
      setTimeout(() => {
        onClose?.();
      }, 700);
    } catch (error) {
      console.error('Password update failed:', error);
      setErrorMsg(error.message || 'Unable to update your password.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleProfileSubmit = async (event) => {
    event.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');
    setDidSaveProfile(false);

    const resolvedLocation = getResolvedLocation();

    if (!resolvedLocation.city) {
      setErrorMsg('Choose a city from autocomplete, or type City, ST.');
      return;
    }

    try {
      setSubmitting(true);
      await updateProfile({
        fullName: [firstName.trim(), lastName.trim()].filter(Boolean).join(' '),
        firstName,
        lastName,
        city: resolvedLocation.city,
        state: resolvedLocation.state,
        country: resolvedLocation.country,
      });
      setSuccessMsg('Profile saved successfully.');
      setDidSaveProfile(true);
    } catch (error) {
      console.error('Profile update failed:', error);
      setErrorMsg(error.message || 'Unable to update your profile.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignOut = async () => {
    setErrorMsg('');
    setSuccessMsg('');

    try {
      setSubmitting(true);
      await signOut();
      setSuccessMsg('Signed out.');
      setTimeout(() => {
        onClose?.();
      }, 400);
    } catch (error) {
      console.error('Sign out failed:', error);
      setErrorMsg(error.message || 'Unable to sign out.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAccount = async () => {
    setErrorMsg('');
    setSuccessMsg('');

    try {
      setSubmitting(true);
      await deleteAccount();
      setSuccessMsg('Your account has been deleted.');
      setShowDeleteConfirm(false);
      setTimeout(() => {
        onClose?.();
      }, 500);
    } catch (error) {
      console.error('Account deletion failed:', error);
      setErrorMsg(error.message || 'Unable to delete your account.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="account-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="account-modal-title"
      ref={dialogRef}
      onMouseDown={handleOverlayClick}
    >
      <div className="account-modal" onMouseDown={(event) => event.stopPropagation()}>
        <button
          className="account-close"
          type="button"
          aria-label="Close account modal"
          onClick={onClose}
          disabled={submitting}
        >
          ×
        </button>

        {isPasswordRecovery ? (
          <>
            <h2 id="account-modal-title" className="account-title">Reset your password</h2>
            <p className="account-subtitle">
              Enter a new password for this account. After saving, you can sign in with it on future visits.
            </p>

            <form className="account-form" onSubmit={handlePasswordResetSubmit}>
              <label className="account-label">
                New password
                <input
                  className="account-input"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="At least 6 characters"
                  autoComplete="new-password"
                />
              </label>

              {errorMsg && <p className="account-error">{errorMsg}</p>}
              {successMsg && <p className="account-success">{successMsg}</p>}

              <button className="account-primary" type="submit" disabled={submitting}>
                {submitting ? 'Saving...' : 'Save new password'}
              </button>
            </form>
          </>
        ) : isAuthenticated ? (
          <>
            <h2 id="account-modal-title" className="account-title">Your account</h2>
            <p className="account-subtitle">
              Your saved events stay attached to this account until you sign out.
            </p>

            <form className="account-form" onSubmit={handleProfileSubmit}>
              <label className="account-label">
                Email
                <input className="account-input" type="email" value={email} disabled readOnly />
              </label>

              <label className="account-label">
                First name
                <input
                  className="account-input"
                  type="text"
                  value={firstName}
                  onChange={(event) => {
                    setFirstName(event.target.value);
                    setDidSaveProfile(false);
                    setSuccessMsg('');
                  }}
                  placeholder="First name"
                  autoComplete="given-name"
                />
              </label>

              <label className="account-label">
                Last name
                <input
                  className="account-input"
                  type="text"
                  value={lastName}
                  onChange={(event) => {
                    setLastName(event.target.value);
                    setDidSaveProfile(false);
                    setSuccessMsg('');
                  }}
                  placeholder="Last name"
                  autoComplete="family-name"
                />
              </label>

              <label className="account-label">
                City
                <CityAutocompleteInput
                  value={cityDisplay}
                  onValueChange={handleLocationInputChange}
                  onSelect={handleLocationSelect}
                  inputId="account-city-profile"
                  placeholder="Search city"
                />
              </label>

              {errorMsg && <p className="account-error">{errorMsg}</p>}
              {successMsg && (
                <p className="account-success" role="status" aria-live="polite">
                  {successMsg}
                </p>
              )}

              <div className="account-actions">
                <button className="account-primary" type="submit" disabled={submitting}>
                  {submitting ? 'Saving...' : didSaveProfile ? 'Saved' : 'Save profile'}
                </button>
                <button className="account-secondary" type="button" onClick={handleSignOut} disabled={submitting}>
                  Sign out
                </button>
              </div>
            </form>

            <div className="account-danger-zone">
              <button
                className="account-danger-button"
                type="button"
                onClick={() => {
                  setErrorMsg('');
                  setSuccessMsg('');
                  setShowDeleteConfirm(true);
                }}
                disabled={submitting}
              >
                Delete account
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="account-tabs" role="tablist" aria-label="Account actions">
              <button
                type="button"
                className={`account-tab ${mode === 'signin' ? 'is-active' : ''}`}
                onClick={() => {
                  setMode('signin');
                  setErrorMsg('');
                  setSuccessMsg('');
                }}
              >
                Sign in
              </button>
              <button
                type="button"
                className={`account-tab ${mode === 'signup' ? 'is-active' : ''}`}
                onClick={() => {
                  setMode('signup');
                  setErrorMsg('');
                  setSuccessMsg('');
                }}
              >
                Create account
              </button>
            </div>

            <h2 id="account-modal-title" className="account-title">
              {mode === 'signup'
                ? 'Create your account'
                : mode === 'forgot'
                  ? 'Recover access'
                  : 'Sign in'}
            </h2>
            <p className="account-subtitle">
              {mode === 'signup'
                ? 'Create an account to keep your saved events synced across visits.'
                : mode === 'forgot'
                  ? 'Your sign-in is the email address you used for this account. Enter it below and we will send a password reset link.'
                  : 'Sign in to restore your saved events and profile.'}
            </p>

            <form className="account-form" onSubmit={handleAuthSubmit}>
              {mode === 'signup' && (
                <>
                  <label className="account-label">
                    First name
                    <input
                      className="account-input"
                      type="text"
                      value={firstName}
                      onChange={(event) => setFirstName(event.target.value)}
                      placeholder="First name"
                      autoComplete="given-name"
                    />
                  </label>

                  <label className="account-label">
                    Last name
                    <input
                      className="account-input"
                      type="text"
                      value={lastName}
                      onChange={(event) => setLastName(event.target.value)}
                      placeholder="Last name"
                      autoComplete="family-name"
                    />
                  </label>

                  <label className="account-label">
                    City
                    <CityAutocompleteInput
                      value={cityDisplay}
                      onValueChange={handleLocationInputChange}
                      onSelect={handleLocationSelect}
                      inputId="account-city-signup"
                      placeholder="Search city"
                    />
                  </label>
                </>
              )}

              <label className="account-label">
                Email
                <input
                  className="account-input"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                />
              </label>

              <label className="account-label">
                {mode === 'forgot' ? 'Password reset' : 'Password'}
                {mode === 'forgot' ? (
                  <p className="account-helper">
                    If you forgot the password, use your account email and we will send a reset link there.
                  </p>
                ) : (
                  <input
                    className="account-input"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="At least 6 characters"
                    autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                  />
                )}
              </label>

              {errorMsg && <p className="account-error">{errorMsg}</p>}
              {successMsg && <p className="account-success">{successMsg}</p>}

              <button className="account-primary" type="submit" disabled={submitting}>
                {submitting
                  ? mode === 'signup'
                    ? 'Creating...'
                    : mode === 'forgot'
                      ? 'Sending...'
                      : 'Signing in...'
                  : mode === 'signup'
                    ? 'Create account'
                    : mode === 'forgot'
                      ? 'Send reset link'
                      : 'Sign in'}
              </button>

              {mode === 'signin' && (
                <button
                  className="account-inline-action"
                  type="button"
                  onClick={() => {
                    setMode('forgot');
                    setErrorMsg('');
                    setSuccessMsg('');
                    setPassword('');
                  }}
                  disabled={submitting}
                >
                  Forgot your sign-in or password?
                </button>
              )}
            </form>
          </>
        )}

        {showDeleteConfirm && (
          <div
            className="account-confirm-overlay"
            role="presentation"
            onMouseDown={() => {
              if (!submitting) setShowDeleteConfirm(false);
            }}
          >
            <div
              className="account-confirm-modal"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="account-delete-title"
              aria-describedby="account-delete-copy"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <h3 id="account-delete-title" className="account-danger-title">Delete account?</h3>
              <p id="account-delete-copy" className="account-danger-copy">
                This action is permanent. Your account, profile, and saved events will be removed.
              </p>
              <div className="account-actions">
                <button
                  className="account-secondary"
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  className="account-danger-button"
                  type="button"
                  onClick={handleDeleteAccount}
                  disabled={submitting}
                >
                  {submitting ? 'Deleting...' : 'Confirm delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
